import React, { useEffect, useState } from 'react';
import { fetchSqlDataWithQuery } from '../../../services/api';
import StandardTable, { createActionColumn } from '../../../components/common/StandardTable';
import { Card, Typography, Spin, Input, message } from 'antd';
import ConstPipingPgTableDetails from './ConstPipingPgTableDetails';

const { Title } = Typography;

const ConstPipingPgDataTables: React.FC = () => {
  const [tableNames, setTableNames] = useState<{ object_name: string; object_type: string; row_count: number | null }[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchTableNames = async () => {
      setLoading(true);
      try {
        // PostgreSQL: list tables and views in schema 'public'. Row count uses pg_class.reltuples estimate.
        const sqlQuery = `
          SELECT t.tablename AS object_name,'TABLE' AS object_type,
                 NULLIF(CAST(c.reltuples AS bigint), 0) AS row_count
          FROM pg_tables t
          JOIN pg_namespace n ON n.nspname = t.schemaname
          LEFT JOIN pg_class c ON c.relname = t.tablename AND c.relnamespace = n.oid
          WHERE t.schemaname = 'public'
          UNION ALL
          SELECT v.viewname AS object_name,
                 'VIEW' AS object_type,
                 NULL AS row_count
          FROM pg_views v
          WHERE v.schemaname = 'public'
          ORDER BY object_name;`;
        const result = await fetchSqlDataWithQuery(sqlQuery);
        let rows: { object_name: string; object_type: string; row_count: number | null }[] = [];
        if (Array.isArray(result) && result.length) {
          if ('object_name' in result[0]) rows = result;
        }
        setTableNames(rows);
      } catch (err: any) {
        message.error('Failed to retrieve PostgreSQL table names!');
      } finally {
        setLoading(false);
      }
    };
    fetchTableNames();
  }, []);

  type RowType = { object_name: string; object_type: string; row_count: number | null };
  const columns: any[] = [
    {
      title: 'Name',
      dataIndex: 'object_name',
      key: 'object_name',
      width: 600,
      sorter: (a: RowType, b: RowType) => a.object_name.localeCompare(b.object_name),
    },
    { title: 'Type', dataIndex: 'object_type', key: 'object_type', width: 100 },
    {
      title: 'Record Count (est.)',
      dataIndex: 'row_count',
      key: 'row_count',
      width: 180,
      sorter: (a: RowType, b: RowType) => (a.row_count || 0) - (b.row_count || 0),
      render: (value: number | null) => (value !== null ? value.toLocaleString() : 'N/A'),
      exportValue: (r: RowType) => r.row_count ?? '',
    },
  ];

  if (selectedTable) {
    return <ConstPipingPgTableDetails tableName={selectedTable} onBack={() => setSelectedTable(null)} />;
  }

  const filtered = tableNames.filter((item) => item.object_name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <Card>
      <Title level={3}>All Tables and Views in PostgreSQL Schema public</Title>
      <Input
        placeholder="Search table names"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ width: 250, marginBottom: 16 }}
      />
      {loading ? (
        <Spin />
      ) : (
        <StandardTable
          style={{ maxWidth: '100%', overflowX: 'auto' }}
          dataSource={filtered.map((row) => ({ key: row.object_name, ...row }))}
          columns={[
            ...columns,
            createActionColumn({
              title: 'Actions',
              showView: true,
              showEdit: false,
              showDelete: false,
              onView: (record) => setSelectedTable(record.object_name),
            }),
          ]}
          rowKey="key"
          bordered
        />
      )}
    </Card>
  );
};

export default ConstPipingPgDataTables;
